import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { AnchorLink } from 'components/anchorLink';
import { DropdownMenuFilter } from 'components/dropdownMenuFilter';
import { ScommesseTableTabs } from 'components/scommesseTable/scommesseTableTabs';
import { ScommesseTableTitle } from 'components/scommesseTable/scommesseTableTitle';
import { TooltipPosition } from 'components/tooltip';
import { Wrapper } from 'components/wrapper';
import { AmbienteContext } from 'context/Ambiente';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { useDynamicTempate, useInfoTipoScommessaInformation } from 'features/sport/hooks';
import { selectSelectedFilter, selectSelectedTopGiocateTab, selectTopGiocateTabs } from 'features/sport/selectors';
import { setSelectedTopGiocateTab, sportSlice } from 'features/sport/sportSlice';
import { useGetLabelByKey } from 'hooks/useLingUI';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import { SportsPiuGiocateTabDto, SportsWidgetPiuGiocateEnum } from 'types/swagger';
import { breakpoint } from 'utility/constant';
import { gtag, isNotEmpty, isTruthy, toSportUrlCase } from 'utility/functions';
import { SportManifestazioniTabs } from 'features/sport/components/SportManifestazioniTabs';
import { SportManifestazioniTabType } from 'features/sport/components/SportManifestazioniTabs/SportManifestazioniTabs';
import { SportTableScommesse } from 'features/sport/components/sportTableScommesse';
import { SportTableScommesseHeadersSticky } from 'features/sport/components/sportTableScommesse/sportTableScommesseHeadersSticky';
import { useSportPiuGiocateHook } from 'features/sport/components/sportPiuGiocate/useSportPiuGiocateHook';
import { selectIsActiveTopLive } from 'features/configuration/selectors';
import { AppFragment } from 'enums/path-fragment';

import styles from './SportPiuGiocate.module.scss';

type SportPiuGiocataProps = {
  slug: string;
  tabList: SportsPiuGiocateTabDto[];
  isNotTopTemplate: boolean;
};

const MyTopGiocate = ({ tabList, slug, isNotTopTemplate }: SportPiuGiocataProps) => {
  const dispatch = useAppDispatch();
  const seeAllLabel = useGetLabelByKey('view-all');
  const filters = useTypedSelector(selectSelectedFilter);
  const { isMobile } = useWindowSize();
  const currentTab = useTypedSelector(selectSelectedTopGiocateTab);

  const isActiveTopLive = useTypedSelector(selectIsActiveTopLive);
  const isLiveActive = useMemo(
    () => ([AppFragment.Live, AppFragment.Sport].some((x) => x === slug) ? isActiveTopLive : true),
    [slug, isActiveTopLive]
  );
  const { selected } = useTypedSelector((st) => selectTopGiocateTabs(st, isLiveActive));

  useEffect(() => {
    dispatch(setSelectedTopGiocateTab('1'));
  }, [slug, filters, dispatch]);

  const [selectedMobileTipoScommessa, setSelectedMobileTipoScommessa] = useState(0);

  const { listTab, selectedTab, infoTipoScommessaKeyListTab, viewAllLink } = useMemo(() => {
    const wTabList = tabList ?? [];
    const currentTabIndex = +currentTab - 1;
    const selectedTab = wTabList[currentTabIndex];
    const infoTipoScommessaKeyListTab = Array.from(new Set(selectedTab?.infoTipoScommessaKeyList));

    const listTab: SportManifestazioniTabType[] = (wTabList ?? [])
      .filter((x) => isNotEmpty(x.idTab))
      .map((tab) => {
        const idTabString = (tab.idTab - 1).toString();
        return {
          idTab: idTabString,
          textTab: tab.textTab,
          textTabTrKey: tab.textTabTrKey,
        };
      });

    let viewAllLink: string | undefined = toSportUrlCase(selectedTab?.contentTabList?.[0]?.slug);

    return { listTab, selectedTab, viewAllLink, currentTabIndex, infoTipoScommessaKeyListTab };
  }, [currentTab, tabList]);

  const { infoTipoScommessaList, infoTipoScommessaKeyList, infoTipoScommessaDescription } =
    useInfoTipoScommessaInformation(infoTipoScommessaKeyListTab);

  const onGtag = useCallback(() => {
    gtag({ section: slug, detail: 'Le più giocate', event: 'Click quota' }, true);
  }, [slug]);

  useEffect(() => {
    setSelectedMobileTipoScommessa(0);
  }, [currentTab]);

  return (
    <SportManifestazioniTabs
      listTab={listTab}
      className={styles.wrapperIsVisible}
      currentTab={currentTab}
      handleClick={(tab) => dispatch(setSelectedTopGiocateTab(tab))}
      gtagAction={(textTab: string) =>
        gtag({ section: slug, detail: textTab, event: 'Click tab Le più giocate' }, true)
      }
    >
      <div className={styles.wrapperGroup} data-testid="TopGiocate">
        <ScommesseTableTabs
          className={classNames(styles.only, styles.mobile)}
          activeIndex={selectedMobileTipoScommessa}
          descriptions={infoTipoScommessaDescription}
          onSelectedItem={setSelectedMobileTipoScommessa}
        />
        <SportTableScommesseHeadersSticky
          infoTipoScommessaKeyList={infoTipoScommessaKeyList}
          className={classNames(styles.only, styles.desktop)}
        />

        {selectedTab?.contentTabList?.map((manifestazione, indexManifestazione) => {
          const isFirst = indexManifestazione === 0;
          const { descrizione: labelSx, descrizioneTrKey: labelSxTrKey } = manifestazione ?? {};
          const { descrizione: labelDx, descrizioneTrKey: labelDxTrKey } =
            infoTipoScommessaDescription?.[selectedMobileTipoScommessa] ?? {};

          return isTruthy(manifestazione?.avvenimentoList?.length) ? (
            <div key={`${manifestazione.descrizione}-${indexManifestazione}`} className={styles.wrapperIsVisible}>
              <ScommesseTableTitle
                labelSx={labelSx}
                labelSxTrKey={labelSxTrKey}
                labelDx={labelDx}
                labelDxTrKey={labelDxTrKey}
                className={classNames(styles.only, styles.mobile)}
                tooltipPosition={isMobile ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                infoTipoScommessaKey={infoTipoScommessaList[selectedMobileTipoScommessa].key}
              />
              <SportTableScommesse
                title={manifestazione.descrizione}
                showTitle={!isMobile}
                titleTrKey={manifestazione.descrizioneTrKey}
                avvenimenti={manifestazione.avvenimentoList ?? []}
                hasBorderSide
                showOnlyBilanciata={selected === SportsWidgetPiuGiocateEnum.Live}
                isTopTemplate={!isNotTopTemplate}
                showTableTitle={!isMobile && isFirst}
                sortAvvenimenti
                hasHeaderScommessa={isFirst}
                infoTipoScommessaKeyList={infoTipoScommessaKeyList}
                selectedIndexInfoTipoScommessa={selectedMobileTipoScommessa}
                gtagAction={onGtag}
              />
            </div>
          ) : null;
        })}
      </div>

      {viewAllLink && (
        <div className={styles.footerTab} suppressHydrationWarning>
          <AnchorLink href={viewAllLink} className={styles.link}>
            {seeAllLabel}
          </AnchorLink>
        </div>
      )}
    </SportManifestazioniTabs>
  );
};

export type SportPiuGiocateProps = {
  title: string;
  isNotTopTemplate: boolean;
};

interface MyTopGiocateTabsProps extends SportPiuGiocateProps {
  isLiveActive: boolean;
}

const MyTopGiocateTabs = memo(function SportPiuGiocate({
  title,
  isLiveActive,
  isNotTopTemplate,
}: MyTopGiocateTabsProps) {
  const { doCheck } = useWindowSize();
  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const { slug, template } = useDynamicTempate<Array<SportsPiuGiocateTabDto>>();
  const { selected, list } = useTypedSelector((st) => selectTopGiocateTabs(st, isLiveActive));

  const { isLoading, doFetch } = useSportPiuGiocateHook();

  if (!isTruthy(list?.length)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <React.Fragment />;
  }

  return (
    <Wrapper
      key={`Sport-Top-Giocate-${slug}-${(list ?? []).length}`}
      bgColorTransparent
      noPaddingRight={isMobile || isOpen || isDesktopXl}
      noPaddingLeft
      noPaddingTop
      noPaddingBottom
    >
      <div className={classNames(styles.container, { [styles.cartOpen]: isOpen })}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>{title}</h2>
          <DropdownMenuFilter
            key={`Options-${slug}-${selected}`}
            disabled={isLoading}
            listOfItems={list}
            selectedItem={selected}
            customClassName={styles.categoryFilter}
            translationPrefix="topgiocate"
            handleClick={doFetch}
          />
        </div>
        <MyTopGiocate
          key={`MyTopGiocate-${slug}-${selected}`}
          slug={slug}
          tabList={template}
          isNotTopTemplate={isNotTopTemplate}
        />
      </div>
    </Wrapper>
  );
});

export const SportPiuGiocate = (props: SportPiuGiocateProps) => {
  const { isReady, isFallback, asPath } = useRouter();

  const isActiveTopLive = useTypedSelector(selectIsActiveTopLive);
  const isLiveActive = useMemo(
    () => ([AppFragment.Live, AppFragment.Sport].some((x) => `/${x}` === asPath) ? isActiveTopLive : true),
    [asPath, isActiveTopLive]
  );

  return (
    <AmbienteContext.Provider
      key={`content-next-events`}
      value={{
        ambiente: 'sport',
        pathScommessa: ['sport', 'topGiocate', 'scommesse'],
        infoAggUpdateAllActionType: sportSlice.actions.updateAllInfoAggTopGiocate.type,
      }}
    >
      <MyTopGiocateTabs
        {...props}
        isLiveActive={isLiveActive}
        key={`MyTopGiocateTabs-${isReady}-${isFallback}-${asPath}`}
      />
    </AmbienteContext.Provider>
  );
};
